import { z } from "zod";
import api, { getBackstageURL, Option, OptionEntity } from "services/api";

import regexCustomURL from "utils/regexCustomURL";

export const categorySchema = z.object({
  id: z.number(),
  name: z
    .string()
    .min(1, "分類名稱為必填項目")
    .max(2, "最長 2 個字")
    .regex(/\p{Script=Han}/u, "只允許輸入中文"),
  url: z
    .string()
    .max(500, "最長 500 個字")
    .regex(regexCustomURL, "只允許輸入英文與數字"),
  icon: z.string(),
  iconFile: z.instanceof(File).optional(),
  display: z.number(),
  sort: z.number(),
});

export type Category = z.infer<typeof categorySchema>;

const extendedApi = api.injectEndpoints({
  endpoints: (build) => ({
    categories: build.query<Category[], void>({
      query: () => getBackstageURL("/categories"),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: "Category" as const,
                id: id,
              })),
              "Category",
            ]
          : ["Category"],
    }),
    category: build.query<Category, string>({
      query: (id) => getBackstageURL(`/categories/${id}`),
      providesTags: (result, error, arg) => [{ type: "Category", id: arg }],
    }),
    categoryEntity: build.query<OptionEntity, void>({
      query: () => getBackstageURL("/categories/options"),
      transformResponse: (data: Option[] = []) => {
        const entity: OptionEntity = {
          ids: [],
          entities: {},
        };

        data.forEach((x) => {
          entity.ids.push(x.value);
          entity.entities[x.value] = x;
        });

        return entity;
      },
    }),
    createCategory: build.mutation<Category, Category>({
      query: (body) => ({
        url: getBackstageURL("/categories"),
        method: "POST",
        body,
      }),
      invalidatesTags: ["Category"],
    }),
    updateCategory: build.mutation<void, Category>({
      query: (body) => ({
        url: getBackstageURL(`/categories/${body.id}`),
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, err, arg) => [{ type: "Category", id: arg.id }],
    }),
    deleteCategory: build.mutation<void, number>({
      query: (id) => ({
        url: getBackstageURL(`/categories/${id}`),
        method: "DELETE",
      }),
      invalidatesTags: (result, err, arg) => [{ type: "Category", id: arg }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useCategoriesQuery,
  useCategoryQuery,
  useCategoryEntityQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
} = extendedApi;
